import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Archive, BarChart3, BookOpen, Building2, Calendar, CalendarDays, CheckSquare, ChevronDown, FileText, Globe, History, LayoutDashboard,
  Settings, ShieldCheck, Stamp, Users,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";

export const NAV = [
  { label: "Dashboard", icon: LayoutDashboard, to: "/", perm: "measure.view" },
  {
    label: "Legislative Registry", icon: FileText, perm: "measure.view",
    children: [
      { label: "All Measures", to: "/registry/all" }, { label: "Ordinances", to: "/registry/ordinances" },
      { label: "Resolutions", to: "/registry/resolutions" }, { label: "Proposed Measures", to: "/registry/proposed" },
      { label: "Amendments and Repeals", to: "/registry/amendments" },
    ],
  },
  { label: "My Tasks", icon: CheckSquare, to: "/tasks", tenant: true },
  {
    label: "Committees", icon: Users, perm: "measure.view",
    children: [
      { label: "Committee Directory", to: "/committees", end: true }, { label: "Referrals", to: "/committees/referrals" },
      { label: "Hearings", to: "/committees/hearings" }, { label: "Committee Reports", to: "/committees/reports" },
    ],
  },
  {
    label: "Sessions", icon: Calendar, perm: "measure.view",
    children: [
      { label: "Session Calendar", to: "/sessions" },
      ...[["Session Agenda", "agenda"], ["Attendance", "attendance"], ["Motions and Voting", "voting"], ["Minutes and Journals", "minutes"]].map(([l, t]) => ({ label: l, to: `/sessions?tab=${t}` })),
    ],
  },
  { label: "Executive Actions", icon: Stamp, to: "/executive-actions", perm: "measure.view" },
  { label: "External Review", icon: ShieldCheck, to: "/external-review", perm: "measure.view" },
  { label: "Publication and Effectivity", icon: BookOpen, to: "/publication", perm: "measure.view" },
  { label: "Legislative Calendar", icon: CalendarDays, to: "/calendar", perm: "measure.view" },
  { label: "Documents and Archive", icon: Archive, to: "/archive", perm: "measure.view" },
  {
    label: "Public Portal", icon: Globe, anyPerm: ["portal.prepare", "portal.approve", "portal.manage", "request.process"],
    children: [
      { label: "Release Queue", to: "/portal/releases", perm: "measure.view" },
      { label: "Public Notices", to: "/portal/notices", perm: "measure.view" },
      { label: "Document Requests", to: "/portal/requests", perm: "request.process" },
      { label: "Portal Settings", to: "/portal/settings", perm: "measure.view" },
    ],
  },
  {
    label: "Reports and Analytics", icon: BarChart3, anyPerm: ["report.view", "report.portal_stats", "audit.view"], tenant: true,
    children: [
      ...[["Executive Summary", "executive_summary"], ["Legislative Performance", "legislative_output"], ["Ordinance and Resolution Reports", "ordinance_registry"],
        ["Legislative Workflow Reports", "workflow"], ["Committee Reports", "committees"], ["Session and Proceedings Reports", "sessions"],
        ["Voting and Attendance Reports", "attendance_voting"], ["Executive Action and Review", "executive_review"], ["Publication and Effectivity", "publication_effectivity"],
        ["Document Archive Reports", "documents"], ["Tasks and Deadlines", "tasks"]].map(([l, k]) => ({ label: l, to: `/reports/${k}`, perm: "report.view" })),
      { label: "Public Access Reports", to: "/reports/public_access", perm: "report.portal_stats" },
      { label: "User Activity and Audit", to: "/reports/audit_activity", perm: "audit.view" },
      { label: "Custom Reports", to: "/reports-custom", perm: "report.custom" },
      { label: "Scheduled Reports", to: "/reports-schedules", perm: "report.schedule" },
      { label: "Export History", to: "/reports-history" },
      { label: "Report Definitions", to: "/reports-definitions" },
    ],
  },
  { label: "Audit Logs", icon: History, to: "/audit", perm: "audit.view" },
  {
    label: "Administration", icon: Settings, anyPerm: ["user.manage", "lgu.configure", "workflow.configure", "numbering.manage"],
    children: [
      { label: "Users and Roles", to: "/admin/users", perm: "user.manage" }, { label: "LGU Settings", to: "/admin/lgu", perm: "lgu.configure" },
      { label: "Legislative Terms", to: "/admin/terms", perm: "lgu.configure" }, { label: "Legislators and Offices", to: "/admin/directory", perm: "lgu.configure" },
      { label: "Workflow Configuration", to: "/admin/workflow", perm: "workflow.configure" }, { label: "Numbering Rules", to: "/admin/numbering", perm: "numbering.manage" },
      { label: "Document Templates", to: "/admin/templates", perm: "lgu.configure" }, { label: "Deadline Rules", to: "/admin/deadlines", perm: "workflow.configure" },
      { label: "Post-approval Rules", to: "/admin/post-approval", perm: "lgu.configure" },
      { label: "Retention Policies", to: "/coming/retention", perm: "retention.manage", soon: true }, { label: "Integrations", to: "/admin/integrations", perm: "lgu.configure" },
    ],
  },
  { label: "LGU Tenants", icon: Building2, to: "/platform/lgus", perm: "platform.manage_lgus" },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function allowed(item, user, can) {
  if (item.tenant && !user.lgu) return false;
  if (item.perm && !can(item.perm)) return false;
  if (item.anyPerm && !item.anyPerm.some(can)) return false;
  return true;
}

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 rounded-md px-3 py-2 text-[13px] transition-colors ${isActive ? "bg-sky-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`;

const Soon = () => <span className="ml-auto rounded bg-slate-800 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-slate-400">Next phase</span>;

function Group({ item, user, can, onNavigate }) {
  const loc = useLocation();
  const kids = item.children.filter((c) => allowed(c, user, can));
  const active = kids.some((c) => loc.pathname.startsWith(c.to));
  const [open, setOpen] = useState(active);
  if (!kids.length) return null;
  const Icon = item.icon;
  return (
    <div>
      <button data-testid={`nav-group-${slug(item.label)}`} onClick={() => setOpen(!open)}
        className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-[13px] transition-colors hover:bg-slate-800 hover:text-white ${active ? "text-white" : "text-slate-300"}`}>
        <Icon className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-left">{item.label}</span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="ml-5 mt-0.5 space-y-0.5 border-l border-slate-800 pl-3">
          {kids.map((c) => (
            <NavLink key={c.to} to={c.to} end={c.end} onClick={onNavigate} data-testid={`nav-item-${slug(c.label)}`}
              className={({ isActive }) => linkCls({ isActive: c.to.includes("?") || c.to === "/sessions" ? loc.pathname + loc.search === c.to : isActive })}>
              <span>{c.label}</span>
              {c.soon && <Soon />}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export function Sidebar({ onNavigate }) {
  const { user, can } = useAuth();
  return (
    <nav className="flex h-full flex-col bg-slate-900" data-testid="sidebar">
      <div className="border-b border-slate-800 px-5 py-5">
        <div className="font-heading text-lg font-bold tracking-tight text-white">OTMS</div>
        <div className="text-[11px] leading-tight text-slate-400">Ordinance Tracking &amp; Management</div>
      </div>
      <div className="flex-1 space-y-0.5 overflow-y-auto px-3 py-4">
        {NAV.filter((i) => allowed(i, user, can)).map((item) =>
          item.children ? (
            <Group key={item.label} item={item} user={user} can={can} onNavigate={onNavigate} />
          ) : (
            <NavLink key={item.to} to={item.to} end={item.to === "/"} onClick={onNavigate} data-testid={`nav-item-${slug(item.label)}`} className={linkCls}>
              <item.icon className="h-4 w-4 shrink-0" />
              <span>{item.label}</span>
              {item.soon && <Soon />}
            </NavLink>
          )
        )}
      </div>
      <div className="border-t border-slate-800 px-5 py-3 text-[10px] leading-snug text-slate-500">MySQL-backed · Times shown in Asia/Manila</div>
    </nav>
  );
}
